import {
  Box,
  Button,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { fetchRates } from "../../api/currencyApi";
import conissym from "../../assets/images/conis-sym.png";
import { calculateProjectCost } from "../../utils/pricingEngine";
import Watermark from "../Watermark";
import CostDistributionPremium from "./CostDistributionPremium";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

/* ------------------ Currency symbols ------------------ */
const CURRENCY_SYMBOL = {
  USD: "$",
  INR: "₹",
  AED: "د.إ",
  EUR: "€",
  GBP: "£",
  CAD: "$",
  AUD: "$",
  SGD: "$",
};

/* ------------------ IP + fallback currency detection ------------------ */
const detectUserCurrencyAdvanced = async () => {
  // 1️⃣ Cached
  const cached = localStorage.getItem("costlix_currency");
  if (cached) return cached;

  // 2️⃣ IP-based (no key)
  try {
    const res = await fetch("https://ipapi.co/json/");
    const data = await res.json();

    if (data?.currency) {
      localStorage.setItem("costlix_currency", data.currency);
      return data.currency;
    }
  } catch {}

  // 3️⃣ Timezone (India fix)
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz === "Asia/Kolkata") return "INR";
  } catch {}

  // 4️⃣ Locale fallback
  try {
    const locale = navigator.languages?.[0] || navigator.language;

    if (locale.includes("IN")) return "INR";
    if (locale.includes("AE")) return "AED";
    if (locale.includes("GB")) return "GBP";
    if (locale.includes("EU")) return "EUR";
  } catch {}

  // 5️⃣ Final fallback
  return "USD";
};

const Step4Result = ({ data, onRestart }) => {
  const [rates, setRates] = useState(null);
  const [currency, setCurrency] = useState("USD");

  /* -------- Detect currency on mount -------- */
  useEffect(() => {
    detectUserCurrencyAdvanced().then(setCurrency);
  }, []);

  /* -------- Fetch exchange rates (Frankfurter API) -------- */
  useEffect(() => {
    fetchRates().then(setRates);
  }, []);

  /* -------- Base cost always in USD -------- */
  const baseCost = useMemo(() => {
    return calculateProjectCost(data);
  }, [data]);

  /* -------- Safe rate resolution -------- */
  const rate = useMemo(() => {
    if (currency === "USD") return 1;
    if (!rates || !rates[currency]) return 1;
    return rates[currency];
  }, [currency, rates]);

  /* -------- Converted values -------- */
  const min = useMemo(() => {
    if (!baseCost) return 0;
    return Math.round(baseCost.min * rate);
  }, [baseCost, rate]);

  const max = useMemo(() => {
    if (!baseCost) return 0;
    return Math.round(baseCost.max * rate);
  }, [baseCost, rate]);

  if (!baseCost) return null;

  const talkToExpert = () => {
    const phone = "919532119007"; // no + sign
    const message = "Hello Costlix, I want to talk to an cost expert.";
    const encodedMessage = encodeURIComponent(message);

    window.open(`https://wa.me/${phone}?text=${encodedMessage}`, "_blank");
  };

  return (
    <Box position="relative" mt={{ xs: 4, md: 6 }}>
      {/* ===== HEADER ===== */}
      <Box
        textAlign="center"
        mb={{ xs: 4, md: 5 }}
        sx={{ maxWidth: 760, mx: "auto" }}
      >
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "var(--color-midnight-60)",
            mb: 0.8,
          }}
        >
          Final Estimate
        </Typography>

        <Typography
          component="h3"
          sx={{
            fontSize: { xs: "1.45rem", sm: "1.6rem", md: "1.85rem" },
            fontWeight: 800,
            lineHeight: 1.25,
            letterSpacing: "-0.015em",
            color: "var(--color-midnight)",
          }}
        >
          Your Project{" "}
          <Box component="span" sx={{ color: "var(--color-fiery)" }}>
            Cost
          </Box>
          ?
        </Typography>

        <Typography
          sx={{
            mt: 1.2,
            fontSize: { xs: "0.9rem", md: "1.05rem" },
            lineHeight: 1.65,
            color: "var(--color-midnight-70)",
          }}
        >
          A realistic estimate based on your scope, features, and delivery
          preferences.
        </Typography>
      </Box>

      <Stack spacing={{ xs: 4, md: 6 }}>
        {/* ===== COST HERO CARD ===== */}
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            overflow: "hidden",
            p: { xs: 2.75, sm: 3.5, md: 4.5 },
            borderRadius: { xs: 2, md: 2 },
            textAlign: "center",
            border: "1px solid var(--color-midnight-15)",
            backgroundColor: "#fff",

            /* 🔥 Premium background image */
            backgroundImage: `url(${conissym})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "bottom center",

            /* subtle inner glow */
            boxShadow:
              "0 30px 80px rgba(15,58,66,0.08), inset 0 1px 0 rgba(255,255,255,0.6)",
          }}
        >
          {/* Soft overlay for readability */}
          {/* <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(246,247,235,0.95) 100%)",
              zIndex: 0,
            }}
          /> */}

          <Stack
            spacing={{ xs: 1.4, md: 1.8 }}
            sx={{ position: "relative", zIndex: 1 }}
          >
            {/* Label */}
            <Typography
              sx={{
                fontSize: { xs: "0.5rem", md: "0.7rem" },
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "var(--color-midnight-60)",
              }}
            >
              Estimated Investment
            </Typography>

            {/* Amount */}
            <Typography
              sx={{
                fontSize: {
                  xs: "1.4rem",
                  sm: "1.3rem",
                  md: "2rem",
                },
                fontWeight: 800,
                lineHeight: 1.15,
                color: "var(--color-midnight)",
                wordBreak: "break-word",
              }}
            >
              {CURRENCY_SYMBOL[currency]} {min.toLocaleString()}{" "}
              <Box
                component="span"
                sx={{ mx: 0.5, color: "var(--color-midnight-60)" }}
              >
                –
              </Box>
              {CURRENCY_SYMBOL[currency]} {max.toLocaleString()}
            </Typography>

            {/* Helper text */}
            <Typography
              sx={{
                fontSize: { xs: "0.68rem", md: "0.80rem" },
                color: "var(--color-midnight-70)",
                // maxWidth: 420,
                // mx: "auto",
                lineHeight: 1.6,
                textAlign: "center",
                textShadow:
                  "0 30px 80px rgba(15,58,66,0.08), inset 0 1px 0 rgba(255,255,255,0.6)",
              }}
            >
              Indicative range based on your selections. Final pricing may vary
              after scope refinement.
            </Typography>

            {/* Currency selector */}
            <Box>
              <Select
                size="small"
                value={currency}
                onChange={(e) => {
                  setCurrency(e.target.value);
                  localStorage.setItem("costlix_currency", e.target.value);
                }}
                sx={{
                  mt: 1,
                  px: 1.2,
                  fontSize: { xs: "0.6rem", md: "0.8rem" },
                  borderRadius: 999,
                  backgroundColor: "#fff",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                }}
              >
                <MenuItem value="USD">USD — United States</MenuItem>
                <MenuItem value="INR">INR — India</MenuItem>
                <MenuItem value="AED">AED — UAE</MenuItem>
                <MenuItem value="EUR">EUR — Europe</MenuItem>
                <MenuItem value="GBP">GBP — United Kingdom</MenuItem>
              </Select>
            </Box>
          </Stack>
        </Paper>

        {/* ===== BREAKDOWN ===== */}
        <CostDistributionPremium />
        {/* ===== INCLUDED SUMMARY ===== */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 3.5 },
            borderRadius: 2,
            position: "relative",
            overflow: "hidden",

            /* Premium surface */
            background:
              "linear-gradient(180deg, #FFFFFF 0%, rgba(246,247,235,0.9) 100%)",

            border: "1px solid var(--color-midnight-15)",
            boxShadow: "0 18px 40px rgba(0,0,0,0.06)",

            /* Accent rail */
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: 4,
              background:
                "linear-gradient(180deg, var(--color-fiery), rgba(233,79,55,0.4))",
              borderTopLeftRadius: 16,
              borderBottomLeftRadius: 16,
            },
          }}
        >
          {/* Header */}
          <Stack direction="row" spacing={1.2} alignItems="center" mb={2}>
            <InfoOutlinedIcon
              sx={{ color: "var(--color-fiery)", fontSize: 20 }}
            />
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1rem", md: "1.05rem" },
                color: "var(--color-midnight)",
              }}
            >
              What this estimate includes
            </Typography>
          </Stack>

          {/* Items */}
          <Stack spacing={1.4}>
            {[
              "Project type & platform selection",
              "Selected features & integrations",
              "UI / UX design complexity",
              "Estimated screens & user roles",
              "Development, testing & deployment",
            ].map((text) => (
              <Stack
                key={text}
                direction="row"
                spacing={1.2}
                alignItems="flex-start"
              >
                <CheckCircleRoundedIcon
                  sx={{
                    fontSize: 18,
                    mt: "2px",
                    color: "var(--color-fiery)",
                  }}
                />

                <Typography
                  sx={{
                    fontSize: { xs: "0.85rem", md: "0.9rem" },
                    lineHeight: 1.6,
                    color: "var(--color-midnight-80)",
                  }}
                >
                  {text}
                </Typography>
              </Stack>
            ))}
          </Stack>

          {/* Trust note */}
          <Typography
            sx={{
              mt: 2.5,
              fontSize: "0.75rem",
              color: "var(--color-midnight-60)",
              lineHeight: 1.5,
            }}
          >
            This estimate is based on your selections and industry benchmarks.
            Final scope and pricing are confirmed after technical review.
          </Typography>
        </Paper>
      </Stack>

      {/* ===== FINAL ACTIONS ===== */}
      <Paper
        elevation={0}
        sx={{
          mt: { xs: 4, md: 5 },
          p: { xs: 2, md: 2 },
          borderRadius: 2,
          backgroundColor: "rgba(246,247,235,0.6)",
          border: "1px solid var(--color-midnight-15)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Button
            variant="outlined"
            fullWidth
            sx={{ maxWidth: { sm: 160, md: "fit-content" } }}
            onClick={() => talkToExpert()}
          >
            Talk to a Cost Expert
          </Button>

          <Button
            variant="contained"
            color="secondary"
            fullWidth
            endIcon={<ArrowForwardRoundedIcon />}
            sx={{
              maxWidth: { sm: 260 },
              fontWeight: 600,
              boxShadow: "0 10px 24px rgba(233,79,55,0.35)",
              position: "relative",
              overflow: "hidden",

              /* 🔥 Subtle pulse */
              animation: "ctaPulse 2.6s ease-in-out infinite",

              "@media (prefers-reduced-motion: reduce)": {
                animation: "none",
              },
            }}
            onClick={onRestart}
          >
            Start New Estimate
          </Button>
        </Stack>
      </Paper>

      {/* ===== Watermark ===== */}
      <Watermark />
    </Box>
  );
};

export default Step4Result;
